<template>
  <div
    class="HomeView a-cover z-0 overflow-hidden bg-full"
    :style="{ backgroundImage: useBgImage && `url('${bgImageUrl}')` }"
  >
    <!-- Background Video -->
    <BackgroundVideo
      ref="videoComp"
      :video-field-group="bgVideoValid ? bgVideo : {}"
      @canplaythrough="onVideoCanPlayThrough"
      @played="onVideoPlayed"
      @paused="onVideoPaused"
    />
    <!-- Play / Pause -->
    <transition name="fade">
      <button
        v-if="videoCanPlayThrough && videoIsPaused !== null"
        id="background-video-playstate-toggle"
        :class="videoPlaystateToggleClasses"
        @click="toggleVideoPlaystate"
        :title="this.htmlDecode(videoPlaystateToggleTitle)"
        :aria-label="this.htmlDecode(videoPlaystateToggleTitle)"
      >
        <transition name="fade">
          <SvgIcon
            class="absolute left-1/2 top-1/2 w-5 h-5 transform-translate-x--1/2-y--1/2"
            :key="videoPlaystateToggleIcon"
            :icon="videoPlaystateToggleIcon"
          />
        </transition>
      </button>
    </transition>
    <!-- Overlay -->
    <div
      ref="overlay"
      class="r-cover overflow-hidden"
    >
      <div class="r-cover overflow-hidden">
        <HomeCentered
          v-if="layoutSlug === 'center-align'"
          :home-page="homePage"
        />
        <HomeLeft
          v-else-if="layoutSlug === 'left-align'"
          :home-page="homePage"
        />
      </div>
    </div>
  </div>
</template>

<script>
import BackgroundVideo from '@/components/BackgroundVideo.vue'
import HomeCentered from '@/components/Home/Layout_Centered.vue'
import HomeLeft from '@/components/Home/Layout_Left.vue'
import SvgIcon from '@/components/SvgIcon.vue'
import { mapGetters } from 'vuex'
import { gsap } from 'gsap'
gsap.defaults({
  duration: 1,
  ease: 'power1.inOut'
})

export default {
  name: 'Home',

  components: {
    BackgroundVideo,
    HomeCentered,
    HomeLeft,
    SvgIcon
  },

  data: () => ({
    videoCanPlayThrough: false,
    videoIsPaused: null
  }),

  computed: {
    // Data
    ...mapGetters([
      'homePage',
      'myStore'
    ]),
    homeStyles() { return this.myStore(['styles', 'home_screen']) ?? {} },
    // Background
    bgImageUrl() {
      const bgImage = this.homePage.background_image ?? {}
      const sizes = bgImage.sizes ?? {}
      return sizes.large
    },
    useBgImage() { return this.bgImageUrl && !this.bgVideoValid },
    bgVideo() { return this.homePage.background_video ?? {} },
    bgVideoValid() { return this.bgVideo.mp4 && this.bgVideo.poster },
    hasBgMedia() { return this.bgImageUrl || this.bgVideoValid },
    overlayStyles() { return this.homeStyles.background ?? {} },
    animationType() { return this.homeStyles.animation_type ?? 'slide-down' },
    // Layout
    layoutSlug() { return this.homeStyles.layout ?? 'center-align' },
    // Video Playstate Toggle Btn
    videoPlaystateToggleTitle() {
      const btnLabels = this.myStore(['labels', 'home_screen', 'background', 'video_playstate_toggle']) ?? {}
      const playingLabels = btnLabels.playing ?? {}
      const playingTitle = playingLabels.btn_title || 'Pause video'
      const pausedLabels = btnLabels.paused
      const pausedTitle = pausedLabels.btn_title || 'Resume video'
      return this.videoIsPaused ? pausedTitle : playingTitle
    },
    videoPlaystateToggleClasses() {
      const backgroundStyles = this.homeStyles.background ?? {}
      const btnStyles = backgroundStyles.video_playstate_toggle ?? {}
      const focusStyles = btnStyles.focus ?? {}
      const hoverStyles = btnStyles.hover ?? {}
      return [
        // GTM
        `background-video-playstate-${this.videoIsPaused ? 'paused' : 'playing'}`,
        // Position
        'absolute right-4 top-4',
        // Dimensions
        'w-12 h-12',
        // Z
        'z-1',
        // Display
        'flex-c',
        // Border
        `border-${btnStyles.border_color ?? 'accent-1'}`,
        `border-${btnStyles.border_width ?? 2}`,
        `rounded-${btnStyles.border_radius ?? 2}`,
        `focus:border-${focusStyles.border_color ?? 'black'} lg:hover:border-${hoverStyles.border_color ?? 'accent-1'}`,
        // Background
        `bg-${btnStyles.bg_color ?? 'white'}`,
        `focus:bg-${focusStyles.bg_color ?? 'white'} lg:hover:bg-${hoverStyles.bg_color ?? 'accent-1'}`,
        // Fill
        `fill-${btnStyles.fill_color ?? 'accent-1'}`,
        `focus:fill-${focusStyles.fill_color ?? 'black'} lg:hover:fill-${hoverStyles.fill_color ?? 'white'}`,
        // Outline
        'focus:outline-none',
        // Transitions
        'transition-colors duration-500 ease-in-out',
      ]
    },
    videoPlaystateToggleIcon() { return this.videoIsPaused ? 'play' : 'pause' },
  },

  created() {
    this.$store.commit('pageMeta', { pageTitle: this.homePage.title })
  },

  mounted() {
    // Welcome animation
    const { animationType } = this
    const $overlay = this.$refs.overlay
    const runAnimation = $overlay && this.hasBgMedia
    if (runAnimation) {
      const initState = {}
      const animationState = { delay: 1.4 }
      // COnfigure
      if (animationType === 'slide-down') {
        initState.y = '-100%'
        animationState.y = '0%'
      } else if (animationType === 'slide-up') {
        initState.y = '100%'
        animationState.y = '0%'
      } else if (animationType === 'fade-in') {
        initState.autoAlpha = 0
        animationState.autoAlpha = 1
      }
      // Run animation
      gsap.fromTo(this.$refs.overlay, initState, animationState)
    }
  },

  methods: {
    // Listeners
    onVideoCanPlayThrough() { this.videoCanPlayThrough = true },
    onVideoPlayed() { this.videoIsPaused = false },
    onVideoPaused() { this.videoIsPaused = true },
    // Action
    toggleVideoPlaystate() {
      if (!this.$refs.videoComp) { return }
      this.$refs.videoComp.togglePlaystate()
    }
  }
}
</script>

